<template>
  <b-container>
    <div
      class="mobile d-flex d-lg-none justify-content-center align-items-center"
    >
      모바일 버전에서는 업로드 할 수 없습니다.
    </div>
    <div class="d-none d-lg-block mb-2">
      <b-card class="my-2">
        <b>TIPS</b>
        <p class="mb-0">
          Your image's format should be <b>jpg</b>, <b>png</b> or
          <b>gif</b>.<br />
          The maximum file size is <b>10 MB</b>.<br />
        </p>
      </b-card>
      <b-form class="d-flex" @submit.prevent="publish">
        <div class="sidebar">
          <b-card>
            <b-form-group>
              <b-form-input
                id="title"
                v-model="artwork.title"
                placeholder="제목"
                maxlength="100"
              ></b-form-input>
              <span class="warning-message" v-if="!canSave && !artwork.title"
                ><font-awesome-icon :icon="['fa', 'exclamation-circle']" />
                제목을 입력하세요</span
              >
            </b-form-group>
            <hr />
            <b-form-group>
              <multi-select
                :title="
                  '장르' +
                    (2 - artwork.categories.length
                      ? ` (${2 - artwork.categories.length}개 선택 가능)`
                      : '')
                "
                v-model="artwork.categories"
                source="category"
                :options="categories"
                :max="2"
                @submit="publish"
              >
                <span slot="checker" slot-scope="{ selectionIndex }">
                  <template v-if="selectionIndex">✓</template>
                  <template v-else>★</template>
                </span>
              </multi-select>
              <span
                class="warning-message"
                v-if="!canSave && !artwork.categories.length"
                ><font-awesome-icon :icon="['fa', 'exclamation-circle']" />
                장르를 1개 이상 선택하세요</span
              >
            </b-form-group>
            <b-form-group>
              <multi-select
                :title="
                  '분야' +
                    (2 - artwork.fields.length
                      ? ` (${2 - artwork.fields.length}개 선택 가능)`
                      : '')
                "
                v-model="artwork.fields"
                source="field"
                :options="fields"
                :max="2"
                @submit="publish"
              >
                <span slot="checker" slot-scope="{ selectionIndex }">
                  <template v-if="selectionIndex">✓</template>
                  <template v-else>★</template>
                </span>
              </multi-select>
              <span
                class="warning-message"
                v-if="!canSave && !artwork.fields.length"
                ><font-awesome-icon :icon="['fa', 'exclamation-circle']" />
                분야를 1개 이상 선택하세요</span
              >
            </b-form-group>
            <b-form-group>
              <multi-select
                :title="
                  '컨셉' +
                    (2 - artwork.concepts.length
                      ? ` (${2 - artwork.concepts.length}개 선택 가능)`
                      : '')
                "
                v-model="artwork.concepts"
                source="concept"
                :options="concepts"
                :max="2"
                @submit="publish"
              >
                <span slot="checker" slot-scope="{ selectionIndex }">
                  <template v-if="selectionIndex">✓</template>
                  <template v-else>★</template>
                </span>
              </multi-select>
              <span
                class="warning-message"
                v-if="!canSave && !artwork.concepts.length"
                ><font-awesome-icon :icon="['fa', 'exclamation-circle']" />
                컨셉을 1개 이상 선택하세요</span
              >
            </b-form-group>
            <b-form-group>
              <color-picker :colors="colors" v-model="artwork.color" />
              <span class="warning-message" v-if="!canSave && !artwork.color"
                ><font-awesome-icon :icon="['fa', 'exclamation-circle']" />
                색상을 선택하세요</span
              >
            </b-form-group>
            <hr />
            <b-form-group>
              <multi-select
                title="저작권"
                v-model="license"
                source="name"
                :options="licenses"
                :max="1"
                @submit="publish"
              >
                <span slot-scope="{ value, selector }">
                  <span class="cc" v-if="selector">
                    <font-awesome-icon
                      :icon="['fab', 'creative-commons-by']"
                      size="2x"
                      v-if="value.attribution"
                    />
                    <font-awesome-icon
                      :icon="['fab', 'creative-commons-nc']"
                      size="2x"
                      v-if="value.non_commercial"
                    />
                    <font-awesome-icon
                      :icon="['fab', 'creative-commons-nd']"
                      size="2x"
                      v-if="value.no_derivative_works"
                    />
                    <font-awesome-icon
                      :icon="['fab', 'creative-commons-sa']"
                      size="2x"
                      v-if="value.share_alike"
                    />
                  </span>
                  <span v-else>
                    {{ value.name }}
                  </span>
                </span>
              </multi-select>
              <span class="warning-message" v-if="!canSave && !license"
                ><font-awesome-icon :icon="['fa', 'exclamation-circle']" />
                저작권을 선택하세요</span
              >
            </b-form-group>
            <hr />
            <b-form-group>
              <tag-input v-model="artwork.tags" />
            </b-form-group>
            <hr />
            <b-form-group class="mb-0">
              <b-form-radio-group v-model="artwork.published">
                <b-form-radio :value="true">공개</b-form-radio>
                <b-form-radio :value="false">비공개</b-form-radio>
              </b-form-radio-group>
            </b-form-group>
          </b-card>
          <b-card class="mt-2 text-center" body-class="p-0 m-0">
            <cropper
              size="container"
              title="Cover Image"
              image-title="일자형 그리드"
              :image="artwork.raw_cover || artwork.cover"
              :aspectRatio="240 / 167"
              @crop="crop"
              @cancel="cancel"
              ref="cropper"
            >
              <b-col
                slot-scope="{ image }"
                class="d-flex flex-column align-items-center p-5 h-100"
              >
                <h5>비율맞춤형 그리드</h5>
                <div class="d-flex align-items-center h-100">
                  <img :src="image" class="img-fluid" />
                </div>
                <b-btn @click="changeCover" class="mt-1">Change Image</b-btn>
              </b-col>
            </cropper>
            <dropzone
              v-model="artwork.cover"
              @click="clickCover"
              ref="dropzone"
            >
              <div class="cover-preview d-flex" v-if="artwork.cover">
                <div class="card-preview">
                  <img :src="artwork.cover" class="img-fluid" />
                  <div class="card-body-preview" />
                </div>
                <div class="card-preview" v-if="artwork.cropped_cover">
                  <img :src="artwork.cropped_cover" class="img-fluid" />
                  <div class="card-body-preview" />
                </div>
              </div>
              <div
                class="cover d-flex justify-content-center align-items-center"
                v-else
              >
                커버 업로드
              </div>
            </dropzone>
            <span class="warning-message" v-if="!canSave && !artwork.cover"
              ><font-awesome-icon :icon="['fa', 'exclamation-circle']" /> 커버
              이미지를 등록하세요</span
            >
          </b-card>
          <div class="submit-button-group">
            <b-button type="submit" class="upload-button" variant="primary"
              >PUBLISHING</b-button
            >
            <b-button type="button" class="preview-button" @click="preview"
              >PREVIEW</b-button
            >
          </div>
        </div>
        <div class="editor">
          <div class="help">
            <b-btn variant="link" id="help">
              <font-awesome-icon icon="question-circle" size="2x" id="help" />
            </b-btn>
            <b-popover target="help" placement="auto" triggers="hover focus">
              pixpills는 1400px을<br />
              최대폭으로 사용합니다.
            </b-popover>
          </div>
          <html-editor
            id="upload"
            v-model="artwork.content"
            :backgroundColor="artwork.background_color || 'white'"
            @upload="upload"
            @autoSave="autoSave"
            @createId="createId"
            @changeBackgroundColor="changeBackgroundColor"
            ref="editor"
          />
        </div>
      </b-form>
      <b-modal
        centered
        body-class="text-center"
        footer-class="justify-content-center"
        hide-header
        ok-only
        ref="premium"
      >
        <p>
          멤버십 미가입 아티스트는 업로드하는 모든 프로젝트가 타인에게 보여지지
          않습니다.
        </p>
        <p>
          Any artist who does not have a membership will not be able to see all
          the projects you upload.
        </p>
      </b-modal>
      <b-modal
        centered
        body-class="text-center"
        footer-class="justify-content-center"
        hide-header
        @ok="() => next()"
        @hidden="() => (next = null)"
        ref="leave"
      >
        <p>
          페이지에서 나가시겠습니까?<br />
          작성된 내용은 저장되지 않습니다.
        </p>
        <p>
          Are you sure you want to leave the page?<br />
          Created content will not saved.
        </p>
      </b-modal>
      <b-modal
        centered
        body-class="text-center"
        footer-class="justify-content-center"
        hide-header
        ok-only
        ref="content"
      >
        <p>
          포트폴리오 컨텐츠가 비어있습니다.<br />
          적어도 한 장의 이미지를 포함하여 컨텐츠를 작성해주세요.
        </p>
        <p>
          Portfolio content is empty.<br />
          Please add at least 1 image.
        </p>
      </b-modal>
      <b-modal
          centered
          body-class="text-center"
          footer-class="justify-content-center"
          hide-header
          ok-only
          ref="session"
      >
        <p>
          세션이 만료되었습니다.<br />
          다시 로그인해주세요.
        </p>
        <p>
          Your session has expired. <br />
          Please log in again to continue.
        </p>
      </b-modal>
      <artwork-modal
        :parent="$route.path"
        :initial-artwork="previewed"
        v-if="previewed"
        ref="preview"
      />
    </div>
  </b-container>
</template>

<script>
import Vue from "vue";
import axios from "axios";
import { Artwork } from "../models";
import HtmlEditor from "../components/HtmlEditor.vue";
import MultiSelect from "../components/MultiSelect";
import Dropzone from "../components/Dropzone";
import ArtworkModal from "../components/ArtworkModal";
import ColorPicker from "../components/ColorPicker";
import TagInput from "../components/TagInput";
import Cropper from "../components/Cropper";

export default Vue.component("upload", {
  components: {
    HtmlEditor,
    MultiSelect,
    Dropzone,
    ArtworkModal,
    ColorPicker,
    TagInput,
    Cropper
  },
  data() {
    return {
      editor: "",
      next: null,
      artwork: null,
      previewed: null,
      categories: window.categories,
      fields: window.fields,
      concepts: window.concepts,
      colors: window.colors,
      license: null,
      licenses: [
        {
          name: "저작자표시",
          attribution: true,
          non_commercial: false,
          no_derivative_works: false,
          share_alike: false
        },
        {
          name: "저작자표시-비영리",
          attribution: true,
          non_commercial: true,
          no_derivative_works: false,
          share_alike: false
        },
        {
          name: "저작자표시-변경금지",
          attribution: true,
          non_commercial: false,
          no_derivative_works: true,
          share_alike: false
        },
        {
          name: "저작자표시-동일조건변경허락",
          attribution: true,
          non_commercial: false,
          no_derivative_works: false,
          share_alike: true
        },
        {
          name: "저작자표시-비영리-동일조건변경허락",
          attribution: true,
          non_commercial: true,
          no_derivative_works: false,
          share_alike: true
        },
        {
          name: "저작자표시-비영리-변경금지",
          attribution: true,
          non_commercial: true,
          no_derivative_works: true,
          share_alike: false
        }
      ],
      canSave: true
    };
  },
  watch: {
    "$route.params.artworkId": {
      async handler() {
        await this.load();
      },
      immediate: true
    },
    artwork() {
      this.license = this.licenses.find(license => {
        return (
          license.attribution === this.artwork.attribution &&
          license.non_commercial === this.artwork.non_commercial &&
          license.no_derivative_works === this.artwork.no_derivative_works &&
          license.share_alike === this.artwork.share_alike
        );
      });
    },
    "artwork.published"(published) {
      if (!this.artwork.author.is_premium && published) {
        this.$refs.premium.show();
      }
    },
    "artwork.cover"() {
      if (this.artwork.cover && !this.artwork.cropped_cover) {
        this.$refs.cropper.makeCropper();
      }
    },
    license() {
      const license = this.license || {
        attribution: false,
        non_commercial: false,
        no_derivative_works: false,
        share_alike: false
      };

      this.artwork.attribution = license.attribution;
      this.artwork.non_commercial = license.non_commercial;
      this.artwork.no_derivative_works = license.no_derivative_works;
      this.artwork.share_alike = license.share_alike;
    }
  },
  mounted() {
    window.addEventListener("beforeunload", this.handleBeforeUnload);
  },
  beforeDestroy() {
    window.removeEventListener("beforeunload", this.handleBeforeUnload);
  },
  beforeRouteLeave(to, from, next) {
    if (to.params.force) {
      next();
      return;
    }

    this.next = next;
    this.$refs.leave.show();
    next(false);
  },
  methods: {
    async load() {
      this.artwork = new Artwork({
        id: this.$route.params.artworkId,
        author: window.user,
        content: "",
        categories: [],
        fields: [],
        concepts: [],
        background_color: "#FFFFFF",
        color: null,
        attribution: false,
        non_commercial: false,
        no_derivative_works: false,
        share_alike: false,
        tags: [],
        published: true,
        draft: true,
        cover: null,
        raw_cover: null,
        cropped_cover: null
      });

      if (!this.$route.params.artworkId) {
        const drafts = await this.artwork.mine({
          draft: true,
          limit: 1
        });
        if (drafts.count) {
          const draft = drafts.results[0];
          const answer = confirm(
              "임시 저장본을 불러오시겠습니까? 취소하면 새로 작성합니다."
          );
          if (answer) {
            this.$router.replace({
              name: "upload-with-artwork",
              params: { artworkId: draft.id, force: true }
            });
          } else {
            await draft.delete();
          }
        }
        return;
      }

      if (!this.artwork.id) {
        this.$nextTick(() => {
          if (!this.artwork.author.is_premium) {
            this.$refs.premium.show();
          }
        });
        return;
      }

      try {
        document.body.classList.add("loading-open");
        this.artwork = await this.artwork.load();
        document.body.classList.remove("loading-open");

        if (!this.artwork.author.is_premium && this.artwork.published) {
          this.$refs.premium.show();
        }
      } catch (error) {
        document.body.classList.remove("loading-open");

        if (error.response && error.response.status === 404) {
          this.$router.replace({ name: "upload", params: { force: "true" } });
        }
      }
    },
    handleBeforeUnload(event) {
      event.returnValue = true;
    },
    async publish() {
      if (
        !(
          this.artwork.title &&
          this.artwork.categories.length &&
          this.artwork.fields.length &&
          this.artwork.concepts.length &&
          this.artwork.color &&
          this.license &&
          this.artwork.cover &&
          this.artwork.cropped_cover
        )
      ) {
        this.canSave = false;
        return;
      }
      this.artwork.draft = false;
      this.canSave = true;

      if (this.artwork.content.indexOf("<img ") === -1) {
        this.$refs.content.show();
        return;
      }

      await this.save();

      await this.$router.push({
        name: "profile",
        params: { slug: window.user.slug, force: "true" }
      });
    },
    async createId(callback) {
      document.body.classList.add("loading-open");

      try {
        if (!this.artwork.id) {
          this.artwork = await this.artwork.save();

          history.replaceState(
              null,
              null,
              this.$router.resolve({
                name: "upload-with-artwork",
                params: {artworkId: this.artwork.id, force: true}
              }).href
          );
        }
        callback();
      } catch (e) {
        this.$refs.session.show();
      } finally {
        document.body.classList.remove("loading-open");
      }
    },
    async autoSave() {
      await this.save();
    },
    async save() {
      document.body.classList.add("loading-open");

      if (!this.artwork.id) {
        const { content } = this.artwork;
        this.artwork.content = null;
        this.artwork = await this.artwork.save();
        this.artwork.content = content;

        history.replaceState(
          null,
          null,
          this.$router.resolve({
            name: "upload-with-artwork",
            params: { artworkId: this.artwork.id, force: true }
          }).href
        );
      }

      await this.$refs.editor.save();
      this.artwork = await this.artwork.save();

      document.body.classList.remove("loading-open");
    },
    async upload(filename, blob, success, failure) {
      try {
        const presigned = await this.artwork.presign(blob, {
          filename,
          type: blob.type
        });

        await axios.put(presigned.url, blob, {
          headers: {
            "Content-Type": blob.type,
            "x-amz-acl": "public-read"
          }
        });

        success(presigned.filename);
      } catch (e) {
        failure();
      } finally {
        document.body.classList.remove("loading-open");
      }
    },
    changeBackgroundColor(backgroundColor) {
      this.artwork.background_color = backgroundColor.hex;
    },
    async discard() {
      await this.artwork.delete();
      this.$router.push({ name: "home", params: { force: "true" } });
    },
    preview() {
      this.previewed = this.artwork;
      this.$nextTick(() => {
        this.$refs.preview.open();
      });
    },
    clickCover(event) {
      if (!this.artwork.cover) {
        return;
      }

      event.stopPropagation();
      this.$refs.cropper.makeCropper();
    },
    changeCover() {
      this.artwork.cover = this.artwork.cropped_cover = this.artwork.raw_cover = null;
      this.$refs.dropzone.$el.click();
    },
    crop(blob) {
      this.artwork.cropped_cover = blob;
    },
    cancel() {
      this.artwork.cover = this.artwork.cropped_cover = this.artwork.raw_cover = null;
    }
  }
});
</script>

<style lang="scss" scoped>
.mobile {
  height: calc(100vh - 496px);
}

hr {
  margin: 16px -20px;
}

.sidebar {
  margin-right: 5px;
  width: 228px;

  .cc svg {
    margin-top: -3px;
    margin-right: 5px;
  }

  .cover-preview {
    padding: 2px;

    .card-preview {
      width: 50%;
      padding: 2px;

      .card-body-preview {
        height: 40px;
        background: url(../assets/card-preview.png) no-repeat;
        background-size: contain;
      }
    }
  }

  .cover {
    height: 159px;
    border: 2px dashed #0087f7;
    border-radius: 5px;
  }
}

.editor {
  flex: 1;

  .help {
    float: right;
    margin: 8px -30px 0px 0px;
  }
}

.submit-button-group {
  display: flex;
  justify-content: space-between;
  flex-direction: row-reverse;
  padding: 4px;

  .upload-button {
    width: 136px;
  }

  .preview-button {
    width: 80px;
  }
}

.warning-message {
  position: absolute;
  border: 1px solid gray;
  border-radius: 5px;
  width: 182px;
  height: 32px;
  margin-top: 4px;
  padding: 6px 12px;
  background-color: white;
  font-size: 12px;
  color: #a80000;
  z-index: 1;
  box-shadow: 2px 2px 8px 0 #888;
}
</style>
